import {
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementReadyEvent,
  StripeExpressCheckoutElementShippingAddressChangeEvent,
  StripeExpressCheckoutElementShippingRateChangeEvent,
} from '@stripe/stripe-js';
import { LoadErrorEvent, OnCancel, StripeCart } from './types';
import { PayResponseBody } from './usePay';

export const mockStripeCart: StripeCart = {
  summary: {
    currency: 'USD',
    subtotal: 23.33,
    total: 28.97,
    estimatedShipping: {
      preference: 'STANDARD',
      standard: 6.34,
      standardExTax: 5.64,
      express: 12.2,
      expressExTax: 10.3,
    },
    tax: null,
    showTaxSeparately: true,
    lineItemsCount: 1,
  },
  appliedCoupon: null,
};

export const mockStripeCartWithTenDollarsOffCoupon: StripeCart = {
  summary: {
    currency: 'USD',
    subtotal: 23.33,
    total: 18.97,
    estimatedShipping: {
      preference: 'STANDARD',
      standard: 6.44,
      standardExTax: 5.64,
      express: 12.2,
      expressExTax: 10.2,
    },
    tax: null,
    showTaxSeparately: true,
    lineItemsCount: 1,
  },
  appliedCoupon: {
    code: '10OFF',
    displayName: 'US$10.00 discount',
    discount: 10,
    type: 'AmountCoupon',
  },
};

export const mockStripeCartWithTwentyPercentOffCoupon: StripeCart = {
  summary: {
    currency: 'USD',
    subtotal: 18.68,
    total: 24.04,
    estimatedShipping: {
      preference: 'STANDARD',
      standard: 5.36,
      standardExTax: 4.66,
      express: 10.8,
      expressExTax: 8.9,
    },
    tax: null,
    showTaxSeparately: true,
    lineItemsCount: 1,
  },
  appliedCoupon: {
    code: '20%OFF',
    displayName: '20% off any product',
    discount: 0,
    type: 'PercentOffCoupon',
  },
};

export const mockStripeCartWithTaxes: StripeCart = {
  summary: {
    currency: 'USD',
    subtotal: 37.8,
    total: 44.08,
    estimatedShipping: {
      preference: 'STANDARD',
      standard: 6.28,
      standardExTax: 5.08,
      express: 16.27,
      expressExTax: 13.77,
    },
    tax: { amount: 3.99, type: 'GST' },
    showTaxSeparately: false,
    lineItemsCount: 2,
  },
  appliedCoupon: null,
};

export const mockStripeCartWithTaxesShownSeparately: StripeCart = {
  summary: {
    currency: 'USD',
    subtotal: 37.8,
    total: 44.08,
    estimatedShipping: {
      preference: 'STANDARD',
      standard: 6.28,
      standardExTax: 5.08,
      express: 16.27,
      expressExTax: 13.77,
    },
    tax: { amount: 3.99, type: 'GST' },
    showTaxSeparately: true,
    lineItemsCount: 2,
  },
  appliedCoupon: {
    code: '20OFF',
    displayName: '20% off any product',
    discount: 0,
    type: 'PercentOffCoupon',
  },
};

export const mockStripeCartWithMultipleLineItemsAndQty: StripeCart = {
  ...mockStripeCart,
  summary: {
    ...mockStripeCart.summary,
    lineItemsCount: 6,
  },
};

export const mockStripeCartWithExpressShipping: StripeCart = {
  summary: {
    currency: 'USD',
    subtotal: 23.33,
    total: 35.53,
    estimatedShipping: {
      preference: 'EXPRESS',
      standard: 5.64,
      standardExTax: 4.84,
      express: 12.2,
      expressExTax: 9.9,
    },
    tax: null,
    showTaxSeparately: true,
    lineItemsCount: 1,
  },
  appliedCoupon: null,
};

export const mockStripeCartWithExpressShippingNotAllowed: StripeCart = {
  summary: {
    currency: 'USD',
    subtotal: 23.33,
    total: 28.97,
    estimatedShipping: {
      preference: 'STANDARD',
      standard: 5.64,
      standardExTax: 4.84,
      express: null,
      expressExTax: null,
    },
    tax: null,
    showTaxSeparately: true,
    lineItemsCount: 1,
  },
  appliedCoupon: null,
};

export const mockPayResponse: PayResponseBody = {
  data: {
    id: 'pi_3NowgQGshnEBcGJA0Xe9nTO3',
    client_secret:
      'pi_3NowgQGshnEBcGJA0Xe9nTO3_secret_zm93kcLVOORRoyQhDqSZqjHJg',
    status: 'requires_payment_method',
    purchase_order_id: 89,
  },
};

export const mockPayResponseWithMissingClientSecret: PayResponseBody = {
  data: {
    id: 'pi_3NowgQGshnEBcGJA0Xe9nTO3',
    client_secret: '',
    status: 'requires_payment_method',
    purchase_order_id: 89,
  },
};

export const mockPayResponseWithValidationError: PayResponseBody = {
  errors: [
    {
      code: 'validation_error',
      title: 'order_details[shipping_details][address][postal_code] is missing',
    },
    {
      code: 'validation_error',
      title: 'order_details[shipping_details][phone_number] is missing',
    },
  ],
};

export const mockPayResponseWithExpressShippingError: PayResponseBody = {
  errors: [
    {
      code: 'invalid_address',
      title: "Express shipping isn't available for PO Boxes.",
    },
  ],
};

export const mockPayResponseWithCouponExpiredError: PayResponseBody = {
  errors: [{ code: 'invalid_coupon', title: 'Coupon expired.' }],
};

export const mockPayResponseWithInvalidAmountError: PayResponseBody = {
  errors: [
    {
      code: 'invalid_amount',
      title:
        'Purchase order amount (59.83) is not within acceptable thresholds.',
    },
  ],
};

export const mockPayResponseWithShippingCountryBlockedError: PayResponseBody = {
  errors: [
    {
      code: 'shipping_country_blocked',
      title:
        "Sorry, we don't deliver to this country. Please select a different shipping country to proceed.",
    },
  ],
};

export const mockPayResponseWithChargeFailedError: PayResponseBody = {
  errors: [
    {
      code: 'charge_failed',
      title:
        'We were unable to process your payment. Please try another payment method',
    },
  ],
};

export const mockPayResponseWithDefaultError: PayResponseBody = {
  errors: [
    {
      code: 'default',
      title: 'An error occurred. Please try again later.',
    },
  ],
};

export const mockPayResponseWithServerError: PayResponseBody = {
  errors: [{ code: 'server_error' }],
};

export const mockStripeConfirmEvent: StripeExpressCheckoutElementConfirmEvent =
  {
    expressPaymentType: 'google_pay',
    billingDetails: {
      name: 'Card Holder Name',
      address: {
        line1: '1600 Amphitheatre Parkway',
        line2: null,
        city: 'Mountain View',
        state: 'CA',
        postal_code: '94043',
        country: 'US',
      },
      email: 'tman-wallet@redbubble.com',
      phone: '+1 650-555-5555',
    },
    shippingAddress: {
      name: 'US User',
      address: {
        line1: '1600 Amphitheatre Parkway1',
        line2: null,
        city: 'Mountain View',
        state: 'CA',
        postal_code: '94043',
        country: 'US',
      },
    },
    shippingRate: {
      id: 'standard',
      amount: 200,
      displayName: 'Standard',
    },
    paymentFailed: (payload?: any) => {
      return payload;
    },
  };

export const mockApplePayStripeClickEvent: StripeExpressCheckoutElementClickEvent =
  {
    elementType: 'expressCheckout',
    expressPaymentType: 'apple_pay',
    resolve: (resolveDetails: any) => {
      return resolveDetails;
    },
  };

export const mockGooglePayStripeClickEvent: StripeExpressCheckoutElementClickEvent =
  {
    elementType: 'expressCheckout',
    expressPaymentType: 'google_pay',
    resolve: (resolveDetails: any) => {
      return resolveDetails;
    },
  };

export const mockStripeLoadErrorEvent: LoadErrorEvent<'expressCheckout'> = {
  elementType: 'expressCheckout',
  error: { type: 'card_error' },
};

export const mockStripeReadyNoWalletsEvent: StripeExpressCheckoutElementReadyEvent =
  {
    elementType: 'expressCheckout',
    availablePaymentMethods: undefined,
  };

export const mockStripeReadyAppleWalletEvent: StripeExpressCheckoutElementReadyEvent =
  {
    elementType: 'expressCheckout',
    availablePaymentMethods: {
      applePay: true,
      googlePay: false,
      amazonPay: false,
      link: false,
      paypal: false,
    },
  };

export const mockStripeReadyGoogleWalletEvent: StripeExpressCheckoutElementReadyEvent =
  {
    elementType: 'expressCheckout',
    availablePaymentMethods: {
      applePay: false,
      googlePay: true,
      amazonPay: false,
      link: false,
      paypal: false,
    },
  };

export const mockStripeReadyBothWalletsEvent: StripeExpressCheckoutElementReadyEvent =
  {
    elementType: 'expressCheckout',
    availablePaymentMethods: {
      applePay: true,
      googlePay: true,
      amazonPay: false,
      link: false,
      paypal: false,
    },
  };

export const mockLineItemsData = [
  {
    name: '1 item',
    amount: 2333,
  },
  {
    name: 'Shipping',
    amount: 564,
  },
];

export const mockShippingRatesData = [
  {
    id: 'STANDARD',
    displayName: 'Standard',
    amount: 634,
  },
  {
    id: 'EXPRESS',
    displayName: 'Express',
    amount: 1220,
  },
];

export const mockStripeCancelEvent: Parameters<OnCancel<'expressCheckout'>>[0] =
  { elementType: 'expressCheckout' };

export const mockStripeShippingRateChangeEvent: StripeExpressCheckoutElementShippingRateChangeEvent =
  {
    shippingRate: {
      id: 'STANDARD',
      amount: 200,
      displayName: 'Standard',
    },
    resolve: () => {},
    reject: () => {},
  };

export const mockStripeShippingAddressChangeEvent: StripeExpressCheckoutElementShippingAddressChangeEvent =
  {
    name: 'John Doe',
    address: {
      city: 'Melbourne',
      state: 'VIC',
      postal_code: '3000',
      country: 'AU',
    },
    resolve: () => {},
    reject: () => {},
  };
